/**
 * <park-gallery-grid> + <park-gallery-card> — the park photo gallery, lifted from
 * components/GalleryPage.astro into the isomorphic park-* contract (epic #138,
 * issue #149; see docs/design-system-rebuild-plan.md).
 *
 * Two elements, one module (like park-field + park-notice):
 *   - <park-gallery-grid> wraps its slotted cards in <div class="gallery-grid">.
 *   - <park-gallery-card> wraps a slotted <Image> in <div class="gallery-item">.
 *
 * Single source of truth = the pure render functions below. They are consumed by
 * BOTH render contexts:
 *   - the live Astro site, via the SSR wrappers components/ds/ParkGalleryGrid.astro
 *     and ParkGalleryCard.astro, which call them server-side and emit the markup
 *     inside <park-gallery-* data-ssr> so the gallery is in the initial HTML
 *     (SEO / no-JS), and
 *   - the Claude-Design preview, where the custom elements render client-side.
 * Because both call the SAME functions, server and client markup are identical.
 *
 * The photo stays SLOTTED — it is the server-rendered EmDash <Image> (srcset),
 * passed through untouched as the card's inner HTML. Emits the live
 * `.gallery-grid` / `.gallery-item` class names; styles live in css/park-gallery.css.
 *
 * Neither element has attributes — they are pure slot wrappers.
 *
 * @dsSpec frontend#FR-7 — the operator-managed photo gallery surface.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, html } from "./base";

const GRID_TAG = "park-gallery-grid";
const CARD_TAG = "park-gallery-card";

/**
 * Pure render: (cards HTML) → inner HTML string. No DOM access, so it is safe to
 * call server-side (the .astro wrapper) and client-side (the element). `cards` is
 * trusted author markup (the slotted <park-gallery-card> elements).
 */
export function renderParkGalleryGrid(cards: string): string {
	return html`<div class="gallery-grid">${cards}</div>`;
}

/**
 * Pure render: (photo HTML) → inner HTML string. `photo` is trusted author markup
 * (the server-rendered EmDash <Image>, kept verbatim with its srcset).
 */
export function renderParkGalleryCard(photo: string): string {
	return html`<div class="gallery-item">${photo}</div>`;
}

class ParkGalleryGrid extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}

	protected render(): string {
		return renderParkGalleryGrid(this.slotHtml);
	}
}

class ParkGalleryCard extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}

	protected render(): string {
		return renderParkGalleryCard(this.slotHtml);
	}
}

defineElement(GRID_TAG, ParkGalleryGrid);
defineElement(CARD_TAG, ParkGalleryCard);

export { ParkGalleryGrid, ParkGalleryCard };
